import { useContext } from '@/compat';
import { ref } from 'vue';
import { clone } from 'ramda';
import { isFunction } from '@/helpers/misc';
import { useDirty } from '@/helpers/misc/useDirty';

import useDialog from './useDialog';
import useConfirm from './useConfirm';

/**
 * This function returns the controls related to dialog.
 * @param {object} params This object contain four parameter.
 * @param {function} params.beforeOpen - This function related to actions to be performed before opening dialog.
 * @param {function} params.onClear - This function related to actions to be taken when closing the dialog.
 * @param {function} params.onClear - This function related to clearing data when closing dialog.
 * @param {array} params.model - This array containing image objects.
 * @returns {object} object - Return useIsolatedDialog functions returns.
 * @example{
 *    const dialog = useIsolatedDialog({
 *       beforeOpen,
 *       onCancel,
 *       onClear,
 *       model,
 *    });
 *
 *    return{
 *      isActive: Dialog open or closed information   //true | false
 *      initialValue: Component's initial value
 *      open: This function that opens dialog
 *      close: This function that close dialog
 *      cancel: This function clearing data and close dialog
 *      clear: This function clearing data
 *      isDirty: Determines whether a value keeps its originality or not   // true | false
 *    }
 * }
 */
const useIsolatedDialog = ({
  beforeOpen, onClear, onShow, onCancel, model,
}) => {
  const { $lang, $i18n } = useContext();

  const initialValue = ref(null);
  const { isDirty, reset: resetDirty } = useDirty(() => initialValue.value, model);

  const dialog = useDialog();

  const openDialog = newValue => {
    if (isFunction(beforeOpen)) {
      beforeOpen(clone(newValue));
    }

    resetDirty();
    initialValue.value = clone(newValue);

    dialog.open();

    if (isFunction(onShow)) {
      onShow(initialValue.value);
    }
  };

  const changeInitialValue = newValue => {
    initialValue.value = clone(newValue);
  };

  const clear = () => {
    if (isFunction(onClear)) {
      onClear();
    }

    resetDirty();
  };

  const closeDialog = () => {
    clear();
    dialog.close();
  };

  const askUserToChangesWillBeLost = useConfirm().warning();

  const cancel = async () => {
    if (isDirty.value && !(await askUserToChangesWillBeLost(
      $i18n.t($lang.app.WARNING),
      $i18n.t($lang.app.YOUR_CHANGES_WILL_BE_LOST_ARE_YOU_SURE),
    ))) {
      return false;
    }

    closeDialog();

    if (isFunction(onCancel)) {
      onCancel();
    }

    return true;
  };

  return {
    isActive: dialog.isActive,
    open: openDialog,
    close: closeDialog,
    cancel,
    clear,
    isDirty,
    initialValue,
    changeInitialValue,
  };
};

export default useIsolatedDialog;
